import React, { ChangeEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, FormProvider, useFormContext } from 'react-hook-form'
import { InseminacaoArtificialForm } from './ArtificialInseminationForm'
import { formatDateToInputValue } from 'src/utils'
import { Button, Input, SelectInput, InputCheck, PageTitle, Loading } from 'src/components'
import { useRegisterCouplePlanning } from './hooks/useRegisterCouplePlanning'
import { MontaTypes, UpdateCouplePlanningDto } from 'src/dto/couple-planning.dto'
import ROUTES from 'src/constants/routes'
import { useStyles } from './styles'
import { SelectAnimalFetchInput } from 'src/components/SelectAnimalFetchInput'
import { FormattedOption } from 'src/dto/animal.dto'
import { VALIDATIONS } from 'src/@types/validations'

export const RegisterCouplePlanning: React.FC = () => {
  const methods = useForm<UpdateCouplePlanningDto>({
    defaultValues: {
      tipoMonta: 'monta'
    },
    resolver: yupResolver(VALIDATIONS.CouplePlanning)
  })

  return (
    <FormProvider {...methods}>
      <RegisterCouplePlanningComponent />
    </FormProvider>
  )
}

const RegisterCouplePlanningComponent: React.FC = () => {
  const style = useStyles()
  const navigate = useNavigate()

  const { setMultipleRegisterCouple, lotList, propertyList, isLoading, onSubmit } = useRegisterCouplePlanning()

  const {
    register,
    watch,
    setValue,
    clearErrors,
    formState: { errors }
  } = useFormContext<UpdateCouplePlanningDto>()

  const isArtificalMontaType = watch('tipoMonta') === 'inseminacaoArtificial'

  return (
    <>
      <div className={style.propertiesHeader}>
        <PageTitle>
          <Link to={ROUTES.COUPLE_PLANNING.ROOT}>Casal Planejado</Link>
          {' > '} Cadastrar Casal
        </PageTitle>
      </div>
      {isLoading ? (
        <div className={style.loadingContainer}>
          <Loading />
        </div>
      ) : (
        <div className={style.propertiesContainer}>
          <form onSubmit={onSubmit}>
            <fieldset className={style.formField}>
              <legend className={style.formTitle}>Cadastrar Casal</legend>
              <span className={style.infoCobertura}>Cobertura Realizada</span>
              <div className={style.checkControl}>
                <InputCheck
                  id='monta'
                  value='monta'
                  label='Monta'
                  name='monta'
                  checked={watch('tipoMonta') === 'monta'}
                  error={errors?.tipoMonta?.message}
                  register={register}
                  onChange={(e) => {
                    const value = e.target.value as MontaTypes
                    setValue('tipoMonta', value)
                    clearErrors('tipoMonta')
                  }}
                />

                <InputCheck
                  id='monta-controladaa'
                  value='montaControlada'
                  label='Monta controlada'
                  name='tipoMonta_2'
                  checked={watch('tipoMonta') === 'montaControlada'}
                  register={register}
                  onChange={(e) => {
                    const value = e.target.value as MontaTypes
                    setValue('tipoMonta', value)
                    clearErrors('tipoMonta')
                  }}
                  className={errors?.tipoMonta?.message ? 'input-check--error' : ''}
                />

                <InputCheck
                  id='inseminação-artificial'
                  value='inseminacaoArtificial'
                  label='Inseminação Artificial'
                  checked={watch('tipoMonta') === 'inseminacaoArtificial'}
                  name='tipoMonta_3'
                  register={register}
                  onChange={(e) => {
                    const value = e.target.value as MontaTypes
                    setValue('tipoMonta', value)
                    clearErrors('tipoMonta')
                  }}
                  className={errors?.tipoMonta?.message ? 'input-check--error' : ''}
                />
              </div>

              <SelectInput
                label='Fazenda'
                selectBy='idFazenda'
                showBy={['descricao']}
                options={propertyList!}
                value={watch('idFazenda') || ''}
                error={errors?.idFazenda?.message}
                placeholder='Selecionar Propriedade'
                onChange={(value: any) => {
                  setValue('idFazenda', value.idFazenda)
                  clearErrors('idFazenda')
                }}
              />

              {isArtificalMontaType ? (
                <>
                  <InseminacaoArtificialForm lotList={lotList!} />
                </>
              ) : (
                <>
                  <div className={style.containerInput}>
                    <SelectAnimalFetchInput
                      className='halfWidth'
                      label='Vaca'
                      selectBy='id'
                      showBy={['cdn', 'nome']}
                      filteredSex='Fêmea'
                      value={watch('idVaca') || ''}
                      hidden={['MT', 'VD']}
                      error={errors?.idVaca?.message}
                      placeholder='Selecionar vaca'
                      onChange={(item: FormattedOption) => {
                        setValue('idVaca', item.id)
                        setValue('nomeVaca', item.animalName!)
                        clearErrors('idVaca')
                      }}
                    />

                    <SelectAnimalFetchInput
                      className='halfWidth'
                      label='Touro'
                      selectBy='id'
                      showBy={['cdn', 'nome']}
                      filteredSex='Macho'
                      hidden={['MT', 'VD']}
                      value={watch('idTouro') || ''}
                      error={errors?.idTouro?.message}
                      placeholder='Selecionar touro'
                      onChange={(item: FormattedOption) => {
                        setValue('idTouro', item.id)
                        setValue('nomeTouro', item.animalName!)
                        clearErrors('idTouro')
                      }}
                    />
                  </div>

                  <Input
                    type='date'
                    max='2999-12-31'
                    label='Data de início'
                    id='data_inicio'
                    error={errors?.dataInicio?.message}
                    onChange={(value: any) => {
                      setValue('dataInicio', value)
                      clearErrors('dataInicio')
                    }}
                    value={formatDateToInputValue(watch('dataInicio') || '')}
                  />

                  <Input
                    type='date'
                    max='2999-12-31'
                    label='Data Final'
                    id='data_final'
                    error={errors?.dataFim?.message}
                    onChange={(value: any) => {
                      setValue('dataFim', value)
                      clearErrors('dataFim')
                    }}
                    value={formatDateToInputValue(watch('dataFim') || '')}
                  />
                </>
              )}

              <div className={style.formFooter}>
                <div className={style.check}>
                  <input
                    id='register_couple'
                    type='checkbox'
                    onChange={(event: ChangeEvent<HTMLInputElement>) => setMultipleRegisterCouple(event.target.checked)}
                  />
                  <label htmlFor='register_couple'>Cadastrar outra cobertura</label>
                </div>

                <div className={style.buttonContainer}>
                  <Button
                    type='button'
                    className={`${style.buttonOutlined} ${style.buttonWrapper}`}
                    onClick={() => navigate(ROUTES.COUPLE_PLANNING.ROOT)}
                    outlined
                  >
                    Cancelar
                  </Button>
                  <Button className={style.buttonWrapper} type='submit'>
                    {watch('idProcesso') ? 'Atualizar' : 'Salvar'}
                  </Button>
                </div>
              </div>
            </fieldset>
          </form>
        </div>
      )}
    </>
  )
}
